import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Flex } from "@chakra-ui/react";
import { AlertDialogModal, Typography } from "@reconvert/react-ui-component";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  closeMissingEntitiesModal,
  selectMissingEntitiesModal,
  setMissingEntitiesSelection,
} from "../../app/slice/missingEntitiesSlice";
import { IFramePostMessageType, sendPostMessage } from "../../utils/helpers";
import { selectConditionsState } from "../../app/slice/conditionSlice";
import { ConditionFact } from "@reconvert/reconvert-utils";
import SearchProductModal from "../CheckoutPage/components/ConditionCreationSidebar/AddConditionView/SearchProductModal";
import ProductTagSelector from "../CheckoutPage/components/ConditionCreationSidebar/ProductTagSelector";
import type { ConditonData } from "../../hooks/useAndOrGrouping";
import { resolvePrevPageOfferOperatorFromSubRule, selectedIdsFromPrevPageOfferSubRule } from "../../utils/getFilterConditionData";

type ListPickerConfig = {
  entity: string;
  conditionFact: ConditionFact;
  titleKey: string;
  helpKey: string;
  labelKey: string;
  isOptionCreatable?: boolean;
  buildSelection: (items: string[], info: string[]) => Record<string, unknown>;
};

const LIST_PICKER_CONFIGS: ListPickerConfig[] = [
  {
    entity: "product_tags",
    conditionFact: ConditionFact.PRODUCT_TAGS,
    titleKey: "Choose Product Tags",
    helpKey: "Select one or more tags from your store",
    labelKey: "Tags",
    isOptionCreatable: true,
    buildSelection: (items) => ({ productTagsSelection: { tags: items } }),
  },
  {
    entity: "product_types",
    conditionFact: ConditionFact.PRODUCT_TYPE,
    titleKey: "Choose Product Types",
    helpKey: "Select one or more product types from your store",
    labelKey: "Product type",
    buildSelection: (items) => ({ productTypesSelection: { types: items } }),
  },
  {
    entity: "product_vendors",
    conditionFact: ConditionFact.PRODUCT_VENDOR,
    titleKey: "Choose Product Vendors",
    helpKey: "Select one or more product vendors from your store",
    labelKey: "Product vendor",
    buildSelection: (items) => ({ productVendorsSelection: { vendors: items } }),
  },
  {
    entity: "segment_ids",
    conditionFact: ConditionFact.CUSTOMER_SEGMENTS,
    titleKey: "Choose Customer Segments",
    helpKey: "Select one or more customer segments from your store",
    labelKey: "Customer Segments",
    buildSelection: (items, info) => ({
      segmentSelection: {
        segments: items.map((id, index) => ({
          id,
          name: info[index] || id,
        })),
      },
    }),
  },
  {
    entity: "customer_languages",
    conditionFact: ConditionFact.CUSTOMER_LANGUAGE,
    titleKey: "Choose Languages",
    helpKey: "Select one or more languages",
    labelKey: "Language",
    buildSelection: (items, info) => ({
      customerLanguagesSelection: { items, itemsInfo: info },
    }),
  },
  {
    entity: "customer_countries",
    conditionFact: ConditionFact.CUSTOMER_COUNTRY,
    titleKey: "Choose Countries",
    helpKey: "Select one or more countries",
    labelKey: "Country",
    buildSelection: (items, info) => ({
      customerCountriesSelection: { items, itemsInfo: info },
    }),
  },
  {
    entity: "sales_channels",
    conditionFact: ConditionFact.PRODUCT_IS_AVAILABLE_AT_SALES_CHANNEL,
    titleKey: "Choose Sales Channels",
    helpKey: "Select one or more sales channels",
    labelKey: "Sales channel",
    buildSelection: (items, info) => ({
      salesChannelsSelection: { items, itemsInfo: info },
    }),
  },
  {
    entity: "payment_methods",
    conditionFact: ConditionFact.PAYMENT_METHOD,
    titleKey: "Choose Payment Methods",
    helpKey: "Select one or more payment methods",
    labelKey: "Payment method",
    buildSelection: (items, info) => ({
      paymentMethodsSelection: { items, itemsInfo: info },
    }),
  },
  {
    entity: "payment_statuses",
    conditionFact: ConditionFact.PAYMENT_STATUS,
    titleKey: "Choose Payment Status",
    helpKey: "Select one or more payment statuses",
    labelKey: "Payment status",
    buildSelection: (items, info) => ({
      paymentStatusesSelection: { items, itemsInfo: info },
    }),
  },
  {
    entity: "fulfillment_statuses",
    conditionFact: ConditionFact.FULFILLMENT_STATUS,
    titleKey: "Choose Fulfillment Status",
    helpKey: "Select one or more fulfillment statuses",
    labelKey: "Fulfillment status",
    buildSelection: (items, info) => ({
      fulfillmentStatusesSelection: { items, itemsInfo: info },
    }),
  },
  {
    entity: "shipping_methods",
    conditionFact: ConditionFact.SHIPPING_METHOD,
    titleKey: "Choose Shipping Methods",
    helpKey: "Select one or more shipping methods",
    labelKey: "Shipping method",
    buildSelection: (items, info) => ({
      shippingMethodsSelection: { items, itemsInfo: info },
    }),
  },
  {
    entity: "order_tags",
    conditionFact: ConditionFact.ORDER_TAGS,
    titleKey: "Choose Order Tags",
    helpKey: "Select one or more order tags",
    labelKey: "Order tags",
    isOptionCreatable: true,
    buildSelection: (items) => ({ orderTagsSelection: { tags: items } }),
  },
  {
    entity: "customer_tags",
    conditionFact: ConditionFact.CUSTOMER_TAGS,
    titleKey: "Choose Customer Tags",
    helpKey: "Select one or more customer tags",
    labelKey: "Customer tags",
    isOptionCreatable: true,
    buildSelection: (items) => ({ customerTagsSelection: { tags: items } }),
  },
  {
    entity: "condition_ids",
    conditionFact: ConditionFact.PREV_PAGE_OFFER,
    titleKey: "Choose Conditions",
    helpKey: "Select one or more conditions from the previous pages",
    labelKey: "Condition list",
    buildSelection: (items, info) => ({
      conditionSelection: {
        conditions: items.map((id, index) => ({
          id,
          name: (info[index] || id).trim(),
        })),
      },
    }),
  },
];

/** Entity keys that open a list picker — derived from configs so needsPicker stays in sync. */
export const LIST_PICKER_ENTITIES: readonly string[] = LIST_PICKER_CONFIGS.map(
  (config) => config.entity
);

/**
 * Global modal opened when the AI chatbot sends a missing-entities message
 * for product, collection, tags, types, vendors, segments, and other list facts.
 */
const MissingEntitiesModal = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const modalRef = useRef<HTMLDivElement>(null);
  const { isOpen, missingEntities, missingContext, selection } = useAppSelector(selectMissingEntitiesModal);
  const { conditions, selectedConditionId } = useAppSelector(selectConditionsState);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [selectedInfo, setSelectedInfo] = useState<string[]>([]);

  const needsProduct =
    missingEntities.includes("product_id") || missingEntities.includes("product_ids");
  const needsCollection = missingEntities.includes("collection_id");
  const listPickerConfig = useMemo(
    () => LIST_PICKER_CONFIGS.find((config) => missingEntities.includes(config.entity)),
    [missingEntities],
  );

  const isProductMode = needsProduct;
  const isCollectionMode = !needsProduct && needsCollection;
  const isListMode = Boolean(listPickerConfig) && !needsProduct && !needsCollection;

  // PREV_PAGE_OFFER picker: ProductTagSelector filters previous-page conditions by the rule's
  // page-selector operator (manual-editor parity). Prefer bot missingContext; on update the
  // LLM often omits it, so fall back to the selected condition's persisted operator / ids.
  const prevPageOfferPickerState = useMemo(() => {
    if (listPickerConfig?.conditionFact !== ConditionFact.PREV_PAGE_OFFER) {
      return { operator: "", selectedIds: [] as string[] };
    }
    const fromContext = missingContext?.condition_ids as { operator?: string; selected?: unknown } | undefined;
    const contextOperator = String(fromContext?.operator || "").trim();
    const contextSelected = Array.isArray(fromContext?.selected)
      ? fromContext.selected.map((id) => String(id || "").trim()).filter(Boolean)
      : [];
    const selected = (conditions || []).find((condition) => condition.id === selectedConditionId);
    const prevSub = selected?.rule?.any?.flatMap((group: { all?: unknown[] }) => group.all || [])
      ?.find((sub: { fact?: string }) => sub.fact === ConditionFact.PREV_PAGE_OFFER);
    const fromSelected = resolvePrevPageOfferOperatorFromSubRule(prevSub);
    const ruleSelected = selectedIdsFromPrevPageOfferSubRule(prevSub);
    return {
      operator: contextOperator || fromSelected || "",
      selectedIds: contextSelected.length ? contextSelected : ruleSelected,
    };
  }, [listPickerConfig, missingContext, conditions, selectedConditionId]);
  const prevPageOfferConditionData = prevPageOfferPickerState.operator
    ? { operator: prevPageOfferPickerState.operator } as ConditonData
    : undefined;
  const preselectedConditionIdsKey = prevPageOfferPickerState.selectedIds.join("\0");

  useEffect(() => {
    if (!isOpen || !isListMode) {
      setSelectedItems([]);
      setSelectedInfo([]);
      return;
    }
    if (listPickerConfig?.entity === "condition_ids") {
      setSelectedItems(preselectedConditionIdsKey ? preselectedConditionIdsKey.split("\0") : []);
      setSelectedInfo([]);
    }
  }, [isOpen, isListMode, listPickerConfig?.entity, preselectedConditionIdsKey]);

  const modalValue: Record<string, string[]> = isProductMode
    ? selection.productSelection ?? {}
    : (selection.collectionSelection ?? []).reduce<Record<string, string[]>>(
        (acc, item) => {
          acc[item.id] = acc[item.id] ?? [];
          acc[item.id].push(item.name);
          return acc;
        },
        {}
      );

  const handleSetValue = useCallback(
    (data: Record<string, string[]>, options?: { productTitles?: Record<string, string> }) => {
      if (isProductMode) {
        const productTitles = options?.productTitles ?? {};

        dispatch(setMissingEntitiesSelection({ productSelection: data }));
        sendPostMessage(IFramePostMessageType.MISSING_ENTITIES_RESPONSE, {
          selection: {
            productSelection: Object.entries(data).map(([productId, variantIds]) => ({
              productId,
              productName: productTitles[productId] ?? "",
              showAllVariants: variantIds.length === 0,
              selectedVariantIds: variantIds,
            })),
          },
        });
      } else {
        const collectionSelection = Object.entries(data).map(([id, names]) => ({
          id,
          name: names?.[0] ?? "",
        }));

        dispatch(setMissingEntitiesSelection({ collectionSelection }));
        sendPostMessage(IFramePostMessageType.MISSING_ENTITIES_RESPONSE, {
          selection: {
            collectionSelection:
              collectionSelection.length > 0
                ? {
                    collectionId: collectionSelection[0].id,
                    collectionName: collectionSelection[0].name,
                  }
                : undefined,
          },
        });
      }

      dispatch(closeMissingEntitiesModal());
    },
    [dispatch, isProductMode]
  );

  const handleClose = useCallback(() => {
    setSelectedItems([]);
    setSelectedInfo([]);
    dispatch(closeMissingEntitiesModal());
  }, [dispatch]);

  const handleListConfirm = useCallback(() => {
    if (!listPickerConfig || selectedItems.length === 0) return;

    const selectionPayload = listPickerConfig.buildSelection(selectedItems, selectedInfo);
    dispatch(setMissingEntitiesSelection(selectionPayload as any));
    sendPostMessage(IFramePostMessageType.MISSING_ENTITIES_RESPONSE, {
      selection: selectionPayload,
    });
    setSelectedItems([]);
    setSelectedInfo([]);
    dispatch(closeMissingEntitiesModal());
  }, [dispatch, listPickerConfig, selectedInfo, selectedItems]);

  if (!isOpen || (!needsProduct && !needsCollection && !listPickerConfig)) {
    return null;
  }

  if (isListMode && listPickerConfig) {
    return (
      <AlertDialogModal
        isOpen={isOpen}
        leastDestructiveRef={modalRef}
        onClose={handleClose}
        title={t(listPickerConfig.titleKey)}
        size="md"
        showCloseButton
        footer={
          <Flex gap="8px" p="16px" justifyContent="end">
            <Button padding="8px 16px" variant="secondary" onClick={handleClose}>
              <Typography variant="subheading2" lineHeight="20px" fontWeight={500}>
                {t("Cancel")}
              </Typography>
            </Button>
            <Button
              padding="8px 16px"
              variant="solid"
              isDisabled={selectedItems.length === 0}
              onClick={handleListConfirm}
            >
              {t("Done")}
            </Button>
          </Flex>
        }
      >
        <Box ref={modalRef} px={4} pb={2}>
          <Typography variant="body" mb={2}>
            {t(listPickerConfig.helpKey)}
          </Typography>
          <ProductTagSelector
            conditionFact={listPickerConfig.conditionFact}
            conditionData={prevPageOfferConditionData}
            label={t(listPickerConfig.labelKey)}
            itemsValue={selectedItems}
            setItemsValue={(data: { items: string | string[]; info: string | string[] }) => {
              const items = Array.isArray(data.items) ? data.items : data.items ? [data.items] : [];
              const info = Array.isArray(data.info) ? data.info : data.info ? [data.info] : items;
              setSelectedItems(items);
              setSelectedInfo(info);
            }}
            isOptionCreatable={Boolean(listPickerConfig.isOptionCreatable)}
            placeholder={t("Select...")}
          />
        </Box>
      </AlertDialogModal>
    );
  }

  return (
    <SearchProductModal
      value={modalValue}
      setValue={handleSetValue}
      handleOnClose={handleClose}
      conditionFact={isProductMode || !isCollectionMode ? ConditionFact.SPECIFIC_PRODUCT : ConditionFact.PRODUCT_COLLECTION}
    />
  );
};

export default MissingEntitiesModal;
